import { useCallback, useMemo, useState } from 'react';
import { Eye, Loader2, Plus, RefreshCw, ShieldOff } from 'lucide-react';
import {
  PortalDsBadge,
  PortalDsButton,
  portalDs,
} from 'src/components/portal-design-system';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'src/components/ui/sheet';
import { DataTable, type DataTableColumn } from 'src/components/shared';
import { formatDisplayDateTime } from 'src/helpers/date.helpers';
import { useGetReportingApiClientDetailQuery } from 'src/features/reporting-api-usage/endpoints/reporting-api-usage.endpoints';
import {
  formatCount,
  formatQuotaConsumption,
  formatRatePercent,
} from 'src/features/reporting-api-usage/utils/reporting-api-usage.utils';
import {
  ReportingApiKey,
  useCreateReportingApiKeyMutation,
  useGetReportingApiKeysQuery,
  useRevealReportingApiKeyMutation,
  useRevokeReportingApiKeyMutation,
  useRotateReportingApiKeyMutation,
} from '../endpoints/reporting-api-keys.endpoints';
import {
  ApisClientKeyDialogs,
  type ApisKeySecretDialogState,
} from './apis-client-key-dialogs';

function sortReportingApiKeys(keys: ReportingApiKey[]): ReportingApiKey[] {
  return [...keys].sort((left, right) => {
    if (left.is_never_used !== right.is_never_used) {
      return left.is_never_used ? -1 : 1;
    }
    return new Date(right.created_at).getTime() - new Date(left.created_at).getTime();
  });
}

interface ApisClientDetailSheetProps {
  open: boolean;
  clientId?: string;
  extClientId?: string;
  clientTitle?: string;
  days: number;
  onOpenChange: (open: boolean) => void;
}

export function ApisClientDetailSheet({
  open,
  clientId,
  extClientId: extClientIdProp,
  clientTitle: clientTitleProp,
  days,
  onOpenChange,
}: ApisClientDetailSheetProps) {
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [pendingRevoke, setPendingRevoke] = useState<ReportingApiKey | null>(null);
  const [pendingRotate, setPendingRotate] = useState<ReportingApiKey | null>(null);
  const [secretDialog, setSecretDialog] = useState<ApisKeySecretDialogState | null>(null);

  const detailQuery = useGetReportingApiClientDetailQuery(
    { clientId: clientId ?? '', days },
    { skip: !open || !clientId }
  );

  const extClientId = extClientIdProp ?? detailQuery.data?.ext_client_id;
  const clientTitle = clientTitleProp ?? detailQuery.data?.client_title ?? 'Client';

  const {
    data: keys = [],
    isLoading: keysLoading,
  } = useGetReportingApiKeysQuery(extClientId ?? '', {
    skip: !open || !extClientId,
  });

  const [createKey, createState] = useCreateReportingApiKeyMutation();
  const [revealKey, revealState] = useRevealReportingApiKeyMutation();
  const [revokeKey, revokeState] = useRevokeReportingApiKeyMutation();
  const [rotateKey, rotateState] = useRotateReportingApiKeyMutation();

  const sortedKeys = useMemo(() => sortReportingApiKeys(keys), [keys]);

  const handleCreate = async() => {
    if (!extClientId || !createName.trim()) {
      return;
    }

    try {
      const result = await createKey({
        ext_client_id: extClientId,
        name: createName.trim(),
      }).unwrap();
      setCreateOpen(false);
      setCreateName('');
      createState.reset();
      setSecretDialog({
        title: 'API key created',
        description: 'Copy this key now. It will not be shown again unless you reveal it from the key list.',
        keyValue: result.key,
        clientTitle: result.client_title,
        scope: result.scope,
      });
    } catch {
      // RTK global error handling surfaces API failures.
    }
  };

  const handleReveal = useCallback(async(key: ReportingApiKey) => {
    try {
      const result = await revealKey(key.id).unwrap();
      revealState.reset();
      setSecretDialog({
        title: 'API key revealed',
        description: `Full key for "${key.name}" (${key.client_title}). Transmit securely and close when finished.`,
        keyValue: result.key,
        clientTitle: key.client_title,
      });
    } catch {
      // RTK global error handling surfaces API failures.
    }
  }, [revealKey, revealState]);

  const handleConfirmRotate = async() => {
    if (!pendingRotate) {
      return;
    }

    try {
      const result = await rotateKey(pendingRotate.id).unwrap();
      setPendingRotate(null);
      rotateState.reset();
      setSecretDialog({
        title: 'API key rotated',
        description: `The previous key for "${pendingRotate.name}" is revoked. Copy the replacement key now.`,
        keyValue: result.key,
        clientTitle: result.client_title,
        scope: result.scope,
      });
    } catch {
      // RTK global error handling surfaces API failures.
    }
  };

  const handleConfirmRevoke = async() => {
    if (!pendingRevoke) {
      return;
    }

    try {
      await revokeKey(pendingRevoke.id).unwrap();
      setPendingRevoke(null);
    } catch {
      // RTK global error handling surfaces API failures.
    }
  };

  const keyColumns = useMemo<DataTableColumn<ReportingApiKey>[]>(() => [
    {
      id: 'name',
      header: 'Name',
      accessor: (row) => row.name,
    },
    {
      id: 'masked_key',
      header: 'Key',
      accessor: (row) => row.masked_key,
      render: (row) => (
        <span className={portalDs.tableCellMono}>
          {row.masked_key}
        </span>
      ),
    },
    {
      id: 'last_used_at',
      header: 'Last used',
      accessor: (row) => row.last_used_at,
      render: (row) => (
        row.is_never_used ? (
          <span className="font-medium text-[#B45309]">
            Never used
          </span>
        ) : (
          formatDisplayDateTime(row.last_used_at ?? undefined)
        )
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessor: (row) => row.status,
      render: (row) => (
        <PortalDsBadge
          variant={row.status === 'active' ? 'green' : 'gray'}
          pill
        >
          {row.status}
        </PortalDsBadge>
      ),
    },
    {
      id: 'actions',
      header: '',
      accessor: () => '',
      render: (row) => (
        <div className="flex justify-end gap-2">
          <PortalDsButton
            type="button"
            variant="secondary"
            size="sm"
            disabled={row.status === 'revoked' || revealState.isLoading}
            onClick={() => void handleReveal(row)}
          >
            <Eye
              className="size-3.5"
              aria-hidden
            />
            Reveal
          </PortalDsButton>
          <PortalDsButton
            type="button"
            variant="secondary"
            size="sm"
            disabled={row.status === 'revoked' || rotateState.isLoading}
            onClick={() => setPendingRotate(row)}
          >
            <RefreshCw
              className="size-3.5"
              aria-hidden
            />
            Rotate
          </PortalDsButton>
          <PortalDsButton
            type="button"
            variant="secondary"
            size="sm"
            disabled={row.status === 'revoked' || revokeState.isLoading}
            className="border-[#FECACA] text-[#DC2626] hover:border-[#FECACA] hover:bg-[#FEF2F2]"
            onClick={() => setPendingRevoke(row)}
          >
            <ShieldOff
              className="size-3.5"
              aria-hidden
            />
            Disable
          </PortalDsButton>
        </div>
      ),
    },
  ], [handleReveal, revealState.isLoading, revokeState.isLoading, rotateState.isLoading]);

  const detail = detailQuery.data;
  const detailLoading = detailQuery.isLoading;

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={onOpenChange}
      >
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 overflow-y-auto bg-white p-0 sm:!w-[70vw] sm:!max-w-none"
        >
          <div className={portalDs.sheetHeader}>
            <SheetHeader className="space-y-1 text-left">
              <SheetTitle className={portalDs.sheetTitle}>
                {clientTitle}
              </SheetTitle>
              <SheetDescription className={portalDs.sheetDescription}>
                {extClientId
                  ? `Review usage and manage API keys for ${extClientId}.`
                  : 'Review usage and manage API keys for this client.'}
              </SheetDescription>
            </SheetHeader>
          </div>

          <div className="flex-1 space-y-8 px-6 py-5">
            <div className="flex justify-end">
              <PortalDsButton
                type="button"
                variant="primary"
                size="sm"
                disabled={!extClientId}
                onClick={() => setCreateOpen(true)}
              >
                <Plus
                  className="size-3.5"
                  aria-hidden
                />
                Create key
              </PortalDsButton>
            </div>

            {detailLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="size-6 animate-spin text-[#2563EB]" />
              </div>
            ) : detailQuery.isError || !detail ? (
              <p className="text-sm text-[#B42318]">
                Unable to load client usage detail.
              </p>
            ) : (
              <>
                <section className="space-y-3">
                  <h3 className="text-sm font-semibold text-[#0D0F1A]">
                    Daily trend
                  </h3>
                  {detail.daily_trend.length === 0 ? (
                    <p className="text-sm text-[#5E6278]">
                      No usage recorded in this window.
                    </p>
                  ) : (
                    <div className="overflow-x-auto rounded-lg border border-[#EDEEF2]">
                      <table className="min-w-full text-sm">
                        <thead className="bg-[#F8F9FB] text-left text-[#4B5060]">
                          <tr>
                            <th className="px-3 py-2">
                              Date
                            </th>
                            <th className="px-3 py-2 text-right">
                              Requests
                            </th>
                            <th className="px-3 py-2 text-right">
                              Cache hit rate
                            </th>
                            <th className="px-3 py-2 text-right">
                              Errors
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.daily_trend.map((point) => (
                            <tr
                              key={point.date}
                              className="border-t border-[#EDEEF2]"
                            >
                              <td className="px-3 py-2">
                                {point.date}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {formatCount(point.request_count)}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {formatRatePercent(point.cache_hit_rate)}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {formatCount(point.error_count)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>

                <section className="space-y-3">
                  <h3 className="text-sm font-semibold text-[#0D0F1A]">
                    Quota consumption
                  </h3>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {detail.quotas.map((quota) => (
                      <div
                        key={quota.name}
                        className="rounded-lg border border-[#EDEEF2] px-3 py-2 text-sm"
                      >
                        <div className="text-[#4B5060]">
                          {quota.name.replaceAll('_', ' ')}
                        </div>
                        <div className="font-medium text-[#0D0F1A]">
                          {formatQuotaConsumption(quota)}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              </>
            )}

            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-[#0D0F1A]">
                API keys
              </h3>
              {keysLoading ? (
                <div className="flex items-center gap-2 text-[#5E6278]">
                  <Loader2
                    className="size-4 animate-spin"
                    aria-hidden
                  />
                  <span className={portalDs.bodySm}>
                    Loading API keys…
                  </span>
                </div>
              ) : (
                <DataTable
                  rows={sortedKeys}
                  columns={keyColumns}
                  getRowKey={(row) => row.id}
                  emptyMessage="No API keys for this client."
                  defaultSort={null}
                  pageSize={10}
                />
              )}
            </section>
          </div>
        </SheetContent>
      </Sheet>

      <ApisClientKeyDialogs
        clientTitle={clientTitle}
        createOpen={createOpen}
        createName={createName}
        createLoading={createState.isLoading}
        secretDialog={secretDialog}
        pendingRevoke={pendingRevoke}
        pendingRotate={pendingRotate}
        revokeLoading={revokeState.isLoading}
        rotateLoading={rotateState.isLoading}
        onCreateOpenChange={setCreateOpen}
        onCreateNameChange={setCreateName}
        onCreate={() => void handleCreate()}
        onSecretDialogChange={setSecretDialog}
        onPendingRevokeChange={setPendingRevoke}
        onPendingRotateChange={setPendingRotate}
        onConfirmRevoke={() => void handleConfirmRevoke()}
        onConfirmRotate={() => void handleConfirmRotate()}
      />
    </>
  );
}
